import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'Qual o mínimo de convidados para contratar?',
    answer: 'O mínimo para eventos presenciais é de 40 convidados. Para encomendas delivery, não há mínimo.'
  },
  {
    question: 'Vocês atendem em qual região?',
    answer: 'Atendemos Belo Horizonte e toda a região metropolitana. Para eventos fora da região, entre em contato para verificar disponibilidade.'
  },
  {
    question: 'O que está incluso no orçamento?',
    answer: 'Equipe completa (maître, barman, garçons, cozinheira, chef, coordenador), estrutura (pratos, talheres, taças, rechaud, guardanapos, toalhas), transporte, montagem e desmontagem. Tudo incluso sem custo extra.'
  },
  {
    question: 'Posso personalizar o cardápio livremente?',
    answer: 'Sim! Nosso sistema é 100% personalizado. Você escolhe exatamente os itens que deseja — churrasco, salgados, petit gourmet, doces, bebidas — sem pacotes fechados.'
  },
  {
    question: 'Como funciona a degustação?',
    answer: 'Após a confirmação do contrato, agendamos uma degustação gratuita para que você aprove todos os itens do cardápio escolhido.'
  },
  {
    question: 'Quais as formas de pagamento?',
    answer: 'Aceitamos PIX, transferência bancária e cartão de crédito com parcelamento em até 10x. Condições especiais para pagamento antecipado.'
  },
  {
    question: 'Com quanto tempo de antecedência devo contratar?',
    answer: 'Recomendamos pelo menos 30 dias de antecedência para garantir a data. Para casamentos e eventos de grande porte, sugerimos 3 a 6 meses.'
  },
  {
    question: 'E se chover no dia do evento ao ar livre?',
    answer: 'Trabalhamos com planos alternativos e temos experiência em adaptações de última hora. Nossa equipe está preparada para qualquer situação.'
  },
  {
    question: 'Vocês fornecem bebidas alcoólicas?',
    answer: 'Sim! Cerveja, chopp, vinho, espumante, whisky, licor e drinks autorais preparados pelo nosso barman profissional. Cada bebida é selecionada individualmente.'
  },
  {
    question: 'O valor do orçamento online é final?',
    answer: 'O valor gerado no simulador é uma estimativa. O valor final é confirmado após conversa com nosso especialista, considerando detalhes específicos do seu evento.'
  },
];

const FaqItem = ({ faq, isOpen, toggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setHeight(contentRef.current?.scrollHeight || 0);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  return (
    <div className={`border-b border-white/5 transition-colors ${
      isOpen ? 'bg-white/[0.015]' : ''
    }`}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left group"
      >
        <span className={`font-body text-sm pr-4 transition-colors ${
          isOpen ? 'text-white' : 'text-white/60 group-hover:text-white/80'
        }`}>
          {faq.question}
        </span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${
          isOpen ? 'text-[#C9A961] rotate-180' : 'text-white/20 group-hover:text-white/40'
        }`} />
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-out"
        style={{ height: `${height}px` }}
      >
        <div ref={contentRef} className="px-5 pb-4">
          <p className="font-body text-sm text-white/50 leading-relaxed">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="faq" className="py-20 md:py-28 bg-[#0a0a0a]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
        }`}>
          <span className="font-body text-xs tracking-[0.3em] uppercase text-[#C9A961]">FAQ</span>
          <h2 className="font-display text-3xl md:text-4xl text-white mt-3 mb-4">
            Perguntas frequentes
          </h2>
          <div className="w-10 h-[2px] bg-gradient-to-r from-[#8B6F47] to-[#C9A961] mx-auto" />
        </div>

        <div className={`rounded-sm border border-white/5 overflow-hidden transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {faqs.map((faq, i) => (
            <FaqItem
              key={i}
              faq={faq}
              isOpen={openIndex === i}
              toggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
